import { prepareProfileMetaContextHeaders } from '@entities/users/apis/meta-profile-context'
import { podoFetcher } from '@server/infrastructure/external-apis/podo/client'
import { BaseResponseSchema } from '@server/schemas'
import { withRequestTimeout } from '@shared/apis/with-request-timeout'
import type { Locale } from '@shared/config/i18n/locales'

import {
  DesignatedTutor,
  FavoriteAvailableSlots,
  FavoriteTutor,
  TutorConnectBookRequest,
  TutorConnectGrant,
  TutorConnectLedgerEntry,
  TutorConnectSummary,
  TutorFavorite,
  TutorMemo,
  TutorReview,
  TutorReviewDelete,
  TutorReviewLike,
  TutorReviewSummary,
  TutorTeachable,
  TutorWeeklyAvailability,
  TutorReservationLimits,
} from '../model/types'

const authHeader = (bearerToken: string, locale?: Locale) => ({
  Authorization: `Bearer ${bearerToken}`,
  ...(locale ? { 'Accept-Language': locale } : {}),
})

export const getTutorConnectSummary = async (bearerToken: string) => {
  return await podoFetcher<BaseResponseSchema<TutorConnectSummary>>({
    url: '/api/v1/tutor-connects/me/summary',
    method: 'GET',
    header: authHeader(bearerToken),
  })
}

export const getTutorConnectEntrySummary = async (bearerToken: string) => {
  return await podoFetcher<BaseResponseSchema<TutorConnectSummary>>({
    url: '/api/v1/tutor-connects/me/entry-summary',
    method: 'GET',
    header: authHeader(bearerToken),
  })
}

export const getTutorConnectGrants = async (bearerToken: string) => {
  return await podoFetcher<BaseResponseSchema<TutorConnectGrant[]>>({
    url: '/api/v1/tutor-connects/me/grants',
    method: 'GET',
    header: authHeader(bearerToken),
  })
}

export const getTutorConnectLedger = async (bearerToken: string) => {
  return await podoFetcher<BaseResponseSchema<TutorConnectLedgerEntry[]>>({
    url: '/api/v1/tutor-connects/me/ledger?includeRevocations=true',
    method: 'GET',
    header: authHeader(bearerToken),
  })
}

export const getTutorConnectFavorites = async (bearerToken: string) => {
  return await podoFetcher<BaseResponseSchema<TutorFavorite[]>>({
    url: '/api/v1/tutor-connects/favorites',
    method: 'GET',
    header: authHeader(bearerToken),
  })
}

export const getAvailableDesignatedTutors = async ({
  bearerToken,
  classId,
  utcStart,
  locale,
}: {
  bearerToken: string
  classId: number
  utcStart: string
  locale?: Locale
}) => {
  return await podoFetcher<BaseResponseSchema<DesignatedTutor[]>>({
    url: `/api/v1/tutor-connects/available-tutors?classId=${classId}&utcStart=${encodeURIComponent(utcStart)}`,
    method: 'GET',
    header: authHeader(bearerToken, locale),
  })
}

export interface BrowseTutorsPageResponse {
  tutors: DesignatedTutor[]
  total: number
  hasNext: boolean
  page: number
  size: number
}

export const getBrowseTutors = async (
  langType: string,
  bearerToken: string,
  page = 0,
  size = 20,
  keyword?: string,
  locale?: Locale,
) => {
  const params = new URLSearchParams({ langType, page: String(page), size: String(size) })
  // 이름 검색어. 공백만 있는 값은 서버 부하만 늘리므로 보내지 않는다(서버도 blank 는 무시).
  const trimmedKeyword = keyword?.trim()
  if (trimmedKeyword) {
    params.set('keyword', trimmedKeyword)
  }
  return await podoFetcher<BaseResponseSchema<BrowseTutorsPageResponse>>({
    url: `/api/v1/tutor-connects/browse-tutors?${params.toString()}`,
    method: 'GET',
    header: authHeader(bearerToken, locale),
  })
}

export const favoriteTutor = async (bearerToken: string, tutorId: number) => {
  return await withRequestTimeout(async (signal) => {
    const result = await podoFetcher<BaseResponseSchema<TutorFavorite>>(
      {
        url: '/api/v1/tutor-connects/favorites',
        method: 'POST',
        body: { tutorId },
        header: authHeader(bearerToken),
      },
      { throwOnErrorResult: true, signal },
    )
    if (result.data?.tutorId !== tutorId || result.data?.favorite !== true) {
      throw new Error('Favorite preference was not confirmed')
    }
    return result
  })
}

export const unfavoriteTutor = async (bearerToken: string, tutorId: number) => {
  return await withRequestTimeout(async (signal) => {
    const result = await podoFetcher<BaseResponseSchema<TutorFavorite>>(
      {
        url: `/api/v1/tutor-connects/favorites/${tutorId}`,
        method: 'DELETE',
        header: authHeader(bearerToken),
      },
      { throwOnErrorResult: true, signal },
    )
    if (result.data?.tutorId !== tutorId || result.data?.favorite !== false) {
      throw new Error('Favorite removal was not confirmed')
    }
    return result
  })
}

export const getTutorMemo = async (bearerToken: string, tutorId: number) => {
  return await podoFetcher<BaseResponseSchema<TutorMemo>>({
    url: `/api/v1/tutor-connects/tutors/${tutorId}/memo`,
    method: 'GET',
    header: authHeader(bearerToken),
  })
}

export const updateTutorMemo = async (bearerToken: string, tutorId: number, memo: string) => {
  return await podoFetcher<BaseResponseSchema<TutorMemo>>({
    url: `/api/v1/tutor-connects/tutors/${tutorId}/memo`,
    method: 'PUT',
    body: { memo },
    header: authHeader(bearerToken),
  })
}

export const getFavoriteTutors = async (bearerToken: string, locale?: Locale) => {
  return await podoFetcher<BaseResponseSchema<FavoriteTutor[]>>({
    url: '/api/v1/tutor-connects/favorite-tutors',
    method: 'GET',
    header: authHeader(bearerToken, locale),
  })
}

export const getTutorReviewSummary = async (bearerToken: string, tutorId: number) => {
  return await podoFetcher<BaseResponseSchema<TutorReviewSummary>>({
    url: `/api/v1/tutor-connects/tutors/${tutorId}/review-summary`,
    method: 'GET',
    header: authHeader(bearerToken),
  })
}

export const getTutorReviews = async (bearerToken: string, tutorId: number, sort?: 'recommend' | 'latest') => {
  // 정렬은 서버가 수행한다(추천순=도움돼요 많은 순). sort 미지정 시 서버 기본값을 따른다.
  const params = new URLSearchParams()
  if (sort) {
    params.set('sort', sort)
  }
  const query = params.toString()
  return await podoFetcher<BaseResponseSchema<TutorReview[]>>({
    url: `/api/v1/tutor-connects/tutors/${tutorId}/reviews${query ? `?${query}` : ''}`,
    method: 'GET',
    header: authHeader(bearerToken),
  })
}

export const likeReview = async (bearerToken: string, reviewId: number) => {
  return await podoFetcher<BaseResponseSchema<TutorReviewLike>>({
    url: `/api/v1/tutor-connects/reviews/${reviewId}/like`,
    method: 'POST',
    header: authHeader(bearerToken),
  })
}

export const unlikeReview = async (bearerToken: string, reviewId: number) => {
  return await podoFetcher<BaseResponseSchema<TutorReviewLike>>({
    url: `/api/v1/tutor-connects/reviews/${reviewId}/like`,
    method: 'DELETE',
    header: authHeader(bearerToken),
  })
}

export const deleteReview = async (bearerToken: string, reviewId: number) => {
  return await podoFetcher<BaseResponseSchema<TutorReviewDelete>>({
    url: `/api/v1/lesson-review/nps/${reviewId}`,
    method: 'DELETE',
    header: authHeader(bearerToken),
  })
}

export const getTutorWeeklyAvailability = async (
  bearerToken: string,
  tutorId: number,
  classId: number,
  utcStart?: string | null,
  days = 7,
) => {
  const params = new URLSearchParams({ classId: String(classId), days: String(days) })
  if (utcStart) {
    params.set('utcStart', utcStart)
  }
  return await podoFetcher<BaseResponseSchema<TutorWeeklyAvailability[]>>({
    url: `/api/v1/tutor-connects/tutors/${tutorId}/weekly-availability?${params.toString()}`,
    method: 'GET',
    header: authHeader(bearerToken),
  })
}

/**
 * classId 없이 튜터 프로필·지정 예약 화면에서 노출하는 "가능 시간".
 * 현재 시각 ~ KST(오늘+7일) 자정 범위의 지정권 허용(allow_designated=1) 슬롯만 내려온다.
 * 이미 예약된(class_id 존재) 슬롯은 slotDetails 에 available=false(마감)로 포함된다 —
 * 화면에서는 "예약 마감" 비활성 셀로 그린다. slots(하위호환)에는 가능 슬롯만 담긴다.
 * subscribeMappId를 전달하면 무제한권 1일 1회 정책을 반영하고, 생략하면 튜터 스케줄만 조회한다.
 * 응답 형태는 weekly-availability 와 동일하다.
 */
export const getTutorOpenSchedule = async (bearerToken: string, tutorId: number, subscribeMappId?: string) => {
  const params = new URLSearchParams()
  if (subscribeMappId) {
    params.set('subscribeMappId', subscribeMappId)
  }
  const query = params.toString()
  return await podoFetcher<BaseResponseSchema<TutorWeeklyAvailability[]>>({
    url: `/api/v1/tutor-connects/tutors/${tutorId}/open-schedule${query ? `?${query}` : ''}`,
    method: 'GET',
    header: authHeader(bearerToken),
  })
}

export const getReservationLimits = async (bearerToken: string, subscribeMappId: string) => {
  const params = new URLSearchParams({ subscribeMappId })
  return await podoFetcher<BaseResponseSchema<TutorReservationLimits>>({
    url: `/api/v1/tutor-connects/reservation-limits?${params.toString()}`,
    method: 'GET',
    header: authHeader(bearerToken),
  })
}

export const getTutorTeachable = async (bearerToken: string, tutorId: number) => {
  return await podoFetcher<BaseResponseSchema<TutorTeachable>>({
    url: `/api/v1/tutor-connects/tutors/${tutorId}/teachable`,
    method: 'GET',
    header: authHeader(bearerToken),
  })
}

export const bookWithDesignatedTutor = async (bearerToken: string, request: TutorConnectBookRequest) => {
  return await podoFetcher<BaseResponseSchema<string>>({
    url: '/api/v1/tutor-connects/book',
    method: 'POST',
    body: request,
    header: { ...authHeader(bearerToken), ...(await prepareProfileMetaContextHeaders(bearerToken)) },
  })
}

export const getFavoriteAvailableSlots = async (
  bearerToken: string,
  classId: number,
  utcStart: string,
  utcEnd: string,
) => {
  const params = new URLSearchParams({
    classId: String(classId),
    utcStart,
    utcEnd,
  })
  return await podoFetcher<BaseResponseSchema<FavoriteAvailableSlots>>({
    url: `/api/v1/tutor-connects/favorite-available-slots?${params.toString()}`,
    method: 'GET',
    header: authHeader(bearerToken),
  })
}
